import { computeClockDistance, isBeyondClockThreshold, metersToFeet } from "@taskhealth/shared";
import { LinearGradient } from "expo-linear-gradient";
import { useEffect, useRef, useState } from "react";
import {
    View,
    Text,
    StyleSheet,
    Pressable,
    Modal,
    ActivityIndicator,
    ScrollView,
    TextInput,
    useWindowDimensions
} from "react-native";

import { useKeyboardOffset } from "@/hooks/useKeyboardOffset";
import type { FormDefinition } from "@/lib/api";
import {
    captureLocation,
    locationBlockCopy,
    openLocationSettings,
    type ClockLocation,
    type LocationBlockReason
} from "@/lib/capture-location";
import { colors, fonts, spacing, radius, shadows, gradients } from "@/lib/theme";

/**
 * Terminal "Submit & clock out" sheet.
 *
 * A visit is only done when it's BOTH submitted and clocked out, so this single
 * action does both:
 *   - All sections complete → submit + clock out.
 *   - Sections incomplete → the deliberate exception: clock out only, and a
 *     reason note is REQUIRED (she's leaving without finishing — that's it,
 *     terminal). The server stores it and admin flags the visit.
 * GPS is captured either way; a "you seem far" step then REQUIRES a location
 * reason before she can finish. It used to be optional and collected almost
 * nothing (3 answers in 90 days across 276 far clock events), which left her
 * with no account on the record when the visit was questioned later and her pay
 * held. The step fires on `isBeyondClockThreshold`, the same rule the server
 * uses to decide a hold, so she is never held over a distance nobody mentioned.
 */
// lat/lng optional: a no-GPS clock-out proceeds with just the `locationNote`
// reason (see the "no GPS" step) and no coordinates.
export type FinishPayload = Partial<ClockLocation> & { locationNote?: string; note?: string };

/** Room kept above a keyboard-lifted sheet for the status bar / notch. */
const TOP_GUTTER = 56;
/** Floor for the sheet on a short screen — below this, the content scrolls. */
const MIN_SHEET_HEIGHT = 260;

interface ClockOutModalProps {
    visible: boolean;
    visit: FormDefinition["visit"];
    patient?: FormDefinition["patient"] | null;
    completedSections: number;
    totalSections: number;
    onConfirm: (payload?: FinishPayload) => Promise<void> | void;
    onCancel: () => void;
    onReturnToAssessment?: () => void;
}

function formatTime(isoString: string | null, timezone: string): string {
    if (!isoString) {
        return "--";
    }
    try {
        return new Date(isoString).toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
            timeZone: timezone
        });
    } catch {
        return "--";
    }
}

function formatDuration(startIso: string | null): string {
    if (!startIso) {
        return "--";
    }
    const diffMs = Date.now() - new Date(startIso).getTime();
    const minutes = Math.round(diffMs / 60000);
    if (minutes < 60) {
        return `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
}

export function ClockOutModal({
    visible,
    visit,
    patient,
    completedSections,
    totalSections,
    onConfirm,
    onCancel,
    onReturnToAssessment
}: ClockOutModalProps) {
    const [loading, setLoading] = useState(false);
    // "noGps" blocks like clock-in: no coordinates → retry or type a reason.
    const [step, setStep] = useState<"confirm" | "far" | "noGps">("confirm");
    const [captured, setCaptured] = useState<ClockLocation | undefined>(undefined);
    const [farDistance, setFarDistance] = useState<number | null>(null);
    // Why the no-GPS step was reached — decides retry-vs-open-settings + copy.
    const [blockReason, setBlockReason] = useState<LocationBlockReason>("no_fix");
    // Optional "why am I far from the patient" reason (GPS soft-step).
    const [locationNote, setLocationNote] = useState("");
    // Required reason when clocking out before the assessment is complete.
    const [incompleteNote, setIncompleteNote] = useState("");
    const tz = visit.timezone ?? "America/New_York";

    // Every step of this sheet can require typing, and the sheet is anchored to
    // the bottom of the screen — exactly where the keyboard opens. A RN `Modal`
    // does not avoid the keyboard on its own (and `KeyboardAvoidingView` is a
    // no-op on the web PWA), so without this the note field and the clock-out
    // button sit UNDER the keyboard: nothing dismisses it (a multiline field has
    // no return-key blur, and the only tappable thing left, the backdrop, cancels
    // the sheet and throws the typing away), so the nurse cannot finish at all
    // and has to ask the office to clock her out. Same fix as ReportIssueModal.
    const keyboardOffset = useKeyboardOffset();
    // Lifting a tall sheet by the keyboard height can push its top off-screen,
    // so cap it at the room actually left and let the overflow scroll.
    const { height: windowHeight } = useWindowDimensions();
    const maxSheetHeight = Math.max(MIN_SHEET_HEIGHT, windowHeight - keyboardOffset - TOP_GUTTER);
    // With the keyboard up, the field being typed into and the button that ends
    // the flow both live at the BOTTOM of the sheet — pin the scroll there.
    const scrollRef = useRef<ScrollView>(null);
    useEffect(() => {
        if (keyboardOffset > 0) {
            scrollRef.current?.scrollToEnd({ animated: true });
        }
    }, [keyboardOffset]);

    const incomplete = totalSections - completedSections;
    const isComplete = totalSections > 0 && completedSections >= totalSections;
    const noteMissing = !isComplete && incompleteNote.trim() === "";

    const reset = () => {
        setStep("confirm");
        setLocationNote("");
        setIncompleteNote("");
    };

    // Single commit path — bundles the captured location, the optional far
    // reason, and (when incomplete) the required exception note.
    const finish = async (location?: ClockLocation) => {
        await onConfirm({
            ...(location ?? {}),
            locationNote: locationNote.trim() || undefined,
            note: !isComplete ? incompleteNote.trim() || undefined : undefined
        } as FinishPayload);
    };

    const runCapture = async () => {
        if (noteMissing) {
            return;
        }
        setLoading(true);
        const cap = await captureLocation();

        // No GPS fix → block on the "no GPS" step (re-prompt / open settings /
        // give a reason) instead of clocking out silently with no coordinates.
        if (!cap.location) {
            setBlockReason(cap.reason);
            setStep("noGps");
            setLoading(false);
            return;
        }
        const location = cap.location;

        if (patient?.lat != null && patient?.lng != null) {
            const distanceInput = {
                nurse: { lat: location.lat, lng: location.lng },
                patient: { lat: patient.lat, lng: patient.lng },
                accuracyFeet: location.accuracy != null ? metersToFeet(location.accuracy) : null
            };
            const result = computeClockDistance(distanceInput);
            // See the matching note in ClockInModal: the prompt must fire on the
            // same rule that can hold her pay, so `isFar`'s past-a-mile cap is
            // not the right test here.
            if (isBeyondClockThreshold(distanceInput)) {
                setCaptured(location);
                setFarDistance(result.distanceFeet);
                setStep("far");
                setLoading(false);
                return;
            }
        }

        await finish(location);
        setLoading(false);
    };

    const confirmAnyway = async () => {
        setLoading(true);
        await finish(captured);
        setLoading(false);
        reset();
    };

    // "no GPS" step: clock out with the required reason and no coordinates.
    const finishNoGps = async () => {
        if (locationNote.trim() === "") {
            return;
        }
        setLoading(true);
        await finish(undefined);
        setLoading(false);
        reset();
    };

    const handleCancel = () => {
        reset();
        onCancel();
    };

    const primaryLabel = isComplete ? "SUBMIT & CLOCK OUT" : "CLOCK OUT WITHOUT SUBMITTING";
    // No longer "ANYWAY": the reason is required now, so by the time this button
    // is live she has already told us why. "Anyway" framed a normal, explained
    // clock-out as her overriding a warning.
    const farPrimaryLabel = isComplete ? "SUBMIT & CLOCK OUT" : "CLOCK OUT";
    const noGpsCopy = locationBlockCopy(blockReason, "out");

    return (
        <Modal visible={visible} transparent animationType="slide" onRequestClose={handleCancel}>
            <View style={styles.backdrop}>
                <Pressable style={styles.backdropTouch} onPress={handleCancel} />
                <View
                    style={[
                        styles.sheet,
                        { maxHeight: maxSheetHeight },
                        keyboardOffset > 0 && { marginBottom: keyboardOffset }
                    ]}
                >
                    <View style={styles.handle} />

                    <ScrollView
                        ref={scrollRef}
                        // "handled" so the first tap on the clock-out button clocks
                        // her out instead of being eaten to dismiss the keyboard.
                        keyboardShouldPersistTaps="handled"
                        showsVerticalScrollIndicator={false}
                        contentContainerStyle={styles.sheetContent}
                    >
                        {step === "far" ? (
                            <>
                                <Text style={styles.title}>You seem far from the patient</Text>
                                {/* Required, not optional — see the matching note in
                                ClockInModal. Clock-OUT is where this matters most:
                                far clock-outs outnumber far clock-ins two to one
                                in production, and almost all of them are "I left
                                and clocked out from the car", which is exactly the
                                sentence we want on the record at the time. */}
                                <Text style={styles.farMessage}>
                                    Your location looks about{" "}
                                    <Text style={styles.farDistance}>
                                        {farDistance != null ? `${farDistance} ft` : "far"}
                                    </Text>{" "}
                                    from the patient&apos;s address. Tell us why and you can still
                                    clock out — this is what we check against later, so a short note
                                    now keeps your pay moving.
                                </Text>

                                <TextInput
                                    style={styles.noteInput}
                                    placeholder="e.g. Finished the visit and clocked out from my car"
                                    placeholderTextColor={colors.textTertiary}
                                    value={locationNote}
                                    onChangeText={setLocationNote}
                                    multiline
                                    maxLength={2000}
                                    editable={!loading}
                                />

                                <Pressable
                                    onPress={confirmAnyway}
                                    disabled={loading || locationNote.trim() === ""}
                                    style={({ pressed }) => [pressed && styles.pressed]}
                                >
                                    <LinearGradient
                                        colors={gradients.teal}
                                        start={{ x: 0, y: 0 }}
                                        end={{ x: 1, y: 1 }}
                                        style={[
                                            styles.confirmButton,
                                            shadows.teal,
                                            locationNote.trim() === "" &&
                                                styles.confirmButtonDisabled
                                        ]}
                                    >
                                        {loading ? (
                                            <ActivityIndicator color="#FFFFFF" />
                                        ) : (
                                            <Text style={styles.confirmText}>
                                                {farPrimaryLabel}
                                            </Text>
                                        )}
                                    </LinearGradient>
                                </Pressable>

                                <Pressable
                                    onPress={() => {
                                        setLocationNote("");
                                        setStep("confirm");
                                        void runCapture();
                                    }}
                                    disabled={loading}
                                    style={styles.cancelButton}
                                >
                                    <Text style={styles.cancelText}>Re-check my location</Text>
                                </Pressable>
                            </>
                        ) : step === "noGps" ? (
                            <>
                                <Text style={styles.title}>{noGpsCopy.title}</Text>
                                <Text style={styles.farMessage}>{noGpsCopy.message}</Text>

                                {noGpsCopy.showSettings ? (
                                    // Permission is denied for good (or Location Services are off) —
                                    // the OS won't re-prompt, so a plain Retry can't help. Deep-link
                                    // to Settings, then let her retry once she's back.
                                    <>
                                        <Pressable
                                            onPress={openLocationSettings}
                                            disabled={loading}
                                            style={({ pressed }) => [pressed && styles.pressed]}
                                        >
                                            <LinearGradient
                                                colors={gradients.teal}
                                                start={{ x: 0, y: 0 }}
                                                end={{ x: 1, y: 1 }}
                                                style={[styles.confirmButton, shadows.teal]}
                                            >
                                                <Text style={styles.confirmText}>
                                                    OPEN PHONE SETTINGS
                                                </Text>
                                            </LinearGradient>
                                        </Pressable>
                                        <Pressable
                                            onPress={() => void runCapture()}
                                            disabled={loading}
                                            style={styles.cancelButton}
                                        >
                                            <Text style={styles.cancelText}>
                                                {loading ? "Checking…" : "I turned it on — retry"}
                                            </Text>
                                        </Pressable>
                                    </>
                                ) : (
                                    <Pressable
                                        onPress={() => void runCapture()}
                                        disabled={loading}
                                        style={({ pressed }) => [pressed && styles.pressed]}
                                    >
                                        <LinearGradient
                                            colors={gradients.teal}
                                            start={{ x: 0, y: 0 }}
                                            end={{ x: 1, y: 1 }}
                                            style={[styles.confirmButton, shadows.teal]}
                                        >
                                            {loading ? (
                                                <ActivityIndicator color="#FFFFFF" />
                                            ) : (
                                                <Text style={styles.confirmText}>
                                                    RETRY LOCATION
                                                </Text>
                                            )}
                                        </LinearGradient>
                                    </Pressable>
                                )}

                                <Text style={styles.noGpsOrLabel}>
                                    or clock out without location
                                </Text>
                                <TextInput
                                    style={styles.noteInput}
                                    placeholder="Reason (required) — e.g. no signal in the building"
                                    placeholderTextColor={colors.textTertiary}
                                    value={locationNote}
                                    onChangeText={setLocationNote}
                                    multiline
                                    maxLength={2000}
                                    editable={!loading}
                                />
                                <Pressable
                                    onPress={finishNoGps}
                                    disabled={loading || locationNote.trim() === ""}
                                    style={styles.cancelButton}
                                >
                                    <Text
                                        style={[
                                            styles.noGpsProceedText,
                                            locationNote.trim() === "" &&
                                                styles.noGpsProceedDisabled
                                        ]}
                                    >
                                        Clock out without location
                                    </Text>
                                </Pressable>

                                <Pressable onPress={handleCancel} style={styles.cancelButton}>
                                    <Text style={styles.cancelText}>Cancel</Text>
                                </Pressable>
                            </>
                        ) : (
                            <>
                                <Text style={styles.title}>
                                    {isComplete
                                        ? "Submit & clock out?"
                                        : "Clock out before finishing?"}
                                </Text>

                                <View style={styles.infoRow}>
                                    <Text style={styles.infoLabel}>Clock In</Text>
                                    <Text style={styles.infoValue}>
                                        {formatTime(visit.clockInAt, tz)}
                                    </Text>
                                </View>

                                <View style={styles.infoRow}>
                                    <Text style={styles.infoLabel}>Now</Text>
                                    <Text style={styles.infoValue}>
                                        {new Date().toLocaleTimeString("en-US", {
                                            hour: "numeric",
                                            minute: "2-digit",
                                            hour12: true,
                                            timeZone: tz
                                        })}
                                    </Text>
                                </View>

                                <View style={styles.infoRow}>
                                    <Text style={styles.infoLabel}>Duration</Text>
                                    <Text style={styles.infoValue}>
                                        {formatDuration(visit.clockInAt)}
                                    </Text>
                                </View>

                                <View style={styles.infoRow}>
                                    <Text style={styles.infoLabel}>Assessment</Text>
                                    <Text style={styles.infoValue}>
                                        {completedSections}/{totalSections} sections
                                    </Text>
                                </View>

                                {isComplete ? (
                                    <Text style={styles.completeHint}>
                                        Your assessment is complete. This submits it and clocks you
                                        out.
                                    </Text>
                                ) : (
                                    <>
                                        <View style={styles.warningBanner}>
                                            <Text style={styles.warningText}>
                                                {incomplete} section{incomplete !== 1 ? "s" : ""}{" "}
                                                incomplete — your assessment won&apos;t be submitted
                                            </Text>
                                        </View>
                                        <Text style={styles.noteLabel}>
                                            Clocking out before finishing is unusual. Please tell us
                                            why (required).
                                        </Text>
                                        <TextInput
                                            style={styles.noteInput}
                                            placeholder="e.g. Patient sent me away / family emergency / I'll finish later"
                                            placeholderTextColor={colors.textTertiary}
                                            value={incompleteNote}
                                            onChangeText={setIncompleteNote}
                                            multiline
                                            maxLength={2000}
                                            editable={!loading}
                                        />
                                    </>
                                )}

                                <Pressable
                                    onPress={runCapture}
                                    disabled={loading || noteMissing}
                                    style={({ pressed }) => [pressed && styles.pressed]}
                                >
                                    <LinearGradient
                                        colors={gradients.teal}
                                        start={{ x: 0, y: 0 }}
                                        end={{ x: 1, y: 1 }}
                                        style={[
                                            styles.confirmButton,
                                            shadows.teal,
                                            noteMissing && styles.confirmButtonDisabled
                                        ]}
                                    >
                                        {loading ? (
                                            <ActivityIndicator color="#FFFFFF" />
                                        ) : (
                                            <Text style={styles.confirmText}>{primaryLabel}</Text>
                                        )}
                                    </LinearGradient>
                                </Pressable>

                                {onReturnToAssessment ? (
                                    <Pressable
                                        onPress={onReturnToAssessment}
                                        style={styles.secondaryButton}
                                    >
                                        <Text style={styles.secondaryText}>
                                            {isComplete
                                                ? "Review Assessment"
                                                : "Return to Assessment"}
                                        </Text>
                                    </Pressable>
                                ) : null}

                                <Pressable onPress={handleCancel} style={styles.cancelButton}>
                                    <Text style={styles.cancelText}>Cancel</Text>
                                </Pressable>
                            </>
                        )}
                    </ScrollView>
                </View>
            </View>
        </Modal>
    );
}

const styles = StyleSheet.create({
    backdrop: {
        flex: 1,
        backgroundColor: "rgba(31, 21, 71, 0.5)",
        justifyContent: "flex-end"
    },
    backdropTouch: {
        flex: 1
    },
    sheet: {
        backgroundColor: colors.surface,
        borderTopLeftRadius: radius["2xl"],
        borderTopRightRadius: radius["2xl"],
        padding: spacing.xl,
        paddingBottom: spacing["3xl"],
        ...shadows.xl
    },
    handle: {
        width: 40,
        height: 4,
        backgroundColor: colors.gray300,
        borderRadius: 2,
        alignSelf: "center",
        marginBottom: spacing.xl
    },
    // Grows with the content until `maxHeight` on the sheet caps it; past that
    // the sheet stops growing and this scrolls.
    sheetContent: {
        flexGrow: 1
    },
    title: {
        fontFamily: fonts.bold,
        fontSize: 18,
        color: colors.textPrimary,
        marginBottom: spacing.base
    },
    infoRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        paddingVertical: spacing.sm,
        borderBottomWidth: 1,
        borderBottomColor: colors.border
    },
    infoLabel: {
        fontFamily: fonts.medium,
        fontSize: 13,
        color: colors.textSecondary
    },
    infoValue: {
        fontFamily: fonts.semiBold,
        fontSize: 13,
        color: colors.textPrimary
    },
    completeHint: {
        fontFamily: fonts.regular,
        fontSize: 13,
        lineHeight: 18,
        color: colors.textSecondary,
        marginTop: spacing.md
    },
    warningBanner: {
        backgroundColor: colors.warning50,
        borderRadius: radius.sm,
        padding: spacing.md,
        marginTop: spacing.md
    },
    noteLabel: {
        fontFamily: fonts.semiBold,
        fontSize: 13,
        color: colors.textPrimary,
        marginTop: spacing.base,
        marginBottom: spacing.sm
    },
    farMessage: {
        fontFamily: fonts.regular,
        fontSize: 14,
        lineHeight: 20,
        color: colors.textSecondary,
        marginBottom: spacing.base
    },
    farDistance: {
        fontFamily: fonts.semiBold,
        color: colors.textPrimary
    },
    noGpsOrLabel: {
        fontFamily: fonts.medium,
        fontSize: 12,
        color: colors.textTertiary,
        textAlign: "center",
        marginTop: spacing.base,
        marginBottom: spacing.sm
    },
    noGpsProceedText: {
        fontFamily: fonts.semiBold,
        fontSize: 14,
        color: colors.warning600
    },
    noGpsProceedDisabled: {
        color: colors.textTertiary
    },
    noteInput: {
        minHeight: 72,
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: radius.lg,
        padding: spacing.base,
        fontFamily: fonts.regular,
        fontSize: 14,
        color: colors.textPrimary,
        textAlignVertical: "top"
    },
    warningText: {
        fontFamily: fonts.semiBold,
        fontSize: 13,
        color: colors.warning600,
        textAlign: "center"
    },
    confirmButton: {
        height: 56,
        borderRadius: radius.full,
        alignItems: "center",
        justifyContent: "center",
        marginTop: spacing.xl
    },
    confirmButtonDisabled: {
        opacity: 0.5
    },
    confirmText: {
        fontFamily: fonts.bold,
        fontSize: 14,
        color: "#FFFFFF",
        letterSpacing: 0.5
    },
    secondaryButton: {
        alignItems: "center",
        paddingTop: spacing.base
    },
    secondaryText: {
        fontFamily: fonts.semiBold,
        fontSize: 14,
        color: colors.teal500
    },
    cancelButton: {
        alignItems: "center",
        paddingTop: spacing.sm
    },
    cancelText: {
        fontFamily: fonts.medium,
        fontSize: 14,
        color: colors.textTertiary
    },
    pressed: {
        opacity: 0.9
    }
});
